import { useMemo, useSyncExternalStore } from 'react';

export interface ThemeDefinition {
  id: string;
  name: string;
  /** Background, primary and highlight colors, for the picker swatches. */
  swatch: [string, string, string];
}

export const THEMES: readonly ThemeDefinition[] = [
  { id: 'ember', name: 'Ember', swatch: ['#10131d', '#ff6a4d', '#ffc94a'] },
  { id: 'lagoon', name: 'Lagoon', swatch: ['#0b1a24', '#2ccfbe', '#ffd866'] },
  { id: 'jungle', name: 'Jungle', swatch: ['#0d1912', '#97d631', '#ffbe5c'] },
  { id: 'nebula', name: 'Nebula', swatch: ['#120e22', '#ff5aa8', '#6fe3ff'] },
  { id: 'sahara', name: 'Sahara', swatch: ['#1a140f', '#f5913a', '#ffd45e'] },
  { id: 'glacier', name: 'Glacier', swatch: ['#0c1520', '#45bbf5', '#ffe27a'] },
  { id: 'aurora', name: 'Aurora', swatch: ['#0b111d', '#2bdc86', '#d49bff'] },
  { id: 'royal', name: 'Royal', swatch: ['#101024', '#f7bf3a', '#ff9bb8'] },
  { id: 'graphite', name: 'Graphite', swatch: ['#121214', '#4d8dff', '#ffd866'] },
  { id: 'cherry', name: 'Cherry', swatch: ['#1a0d12', '#ff4d6d', '#ffcd7a'] },
];

const STORAGE_KEY = 'okwhere:theme';
const DEFAULT_THEME = 'ember';
const listeners = new Set<() => void>();

function storedTheme(): string {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value && THEMES.some((theme) => theme.id === value) ? value : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Applies a theme to the page. Index.html applies the stored one before first paint. */
function apply(id: string): void {
  document.documentElement.dataset.theme = id;
  const background = getComputedStyle(document.documentElement).getPropertyValue('--color-ink-950').trim();
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', background);
}

let current = storedTheme();

export function initTheme(): void {
  apply(current);
}

export function setTheme(id: string): void {
  current = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Storage unavailable: the theme still applies for this visit.
  }
  apply(id);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): string {
  return useSyncExternalStore(subscribe, () => current);
}

/** Resolves a theme color for places that can't use CSS variables (map layers, canvas). */
export function themeColor(variable: `--color-${string}`): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim() || '#ffffff';
}

/** Theme colors needed outside CSS, recomputed only when the theme changes. */
export function useThemeColors(): { primary: string; highlight: string } {
  const theme = useTheme();
  return useMemo(
    () => ({ primary: themeColor('--color-primary-500'), highlight: themeColor('--color-highlight-400') }),
    // `theme` isn't read directly, but the CSS variables depend on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  );
}
